// Reddit conversion pixel — dormant until configured.
//
// Gated behind REACT_APP_REDDIT_PIXEL_ID exactly the way plausible.ts is gated behind
// REACT_APP_PLAUSIBLE_DOMAIN: with no ID set at build time no script is injected and
// every function here is a no-op, so the app ships making no third-party request.
//
// Why it exists. The Reddit carousel campaign runs on the TRAFFIC objective and its
// report shows `Goal: -` and `Total Results: 0`, because Reddit cannot see a signup.
// Plausible records the same conversion on our side, so this is not about counting —
// it is about giving Reddit's optimiser a signal, which is the prerequisite for ever
// moving the campaign off TRAFFIC onto SIGN_UP.
//
// To turn it on:
//   1. Events Manager -> copy the pixel ID (format a2_xxxxxxxx).
//   2. Set REACT_APP_REDDIT_PIXEL_ID in the build env and redeploy.
//
// Deliberately NOT sent: the advanced-matching fields Reddit's snippet accepts
// (`email`, `externalId`, `aaid`, `idfa`). Handing a visitor's email address to an ad
// network to improve our match rate is not something to do quietly as a side effect of
// creating an account. If we ever want it, it is its own decision with its own notice.

/** The standard events Reddit recognises. A typo here records nothing and reports
 *  nothing, so the union exists to make a wrong name a compile error. */
export type RedditEvent =
  | 'PageVisit'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Purchase'
  | 'Lead'
  | 'SignUp';

type RdtFn = {
  (...args: unknown[]): void;
  sendEvent?: (...args: unknown[]) => void;
  callQueue?: unknown[][];
};

declare global {
  interface Window {
    rdt?: RdtFn;
  }
}

const PIXEL_ID = process.env.REACT_APP_REDDIT_PIXEL_ID;
const SRC = 'https://www.redditstatic.com/ads/pixel.js';

/** True when a pixel ID is configured. */
export const redditPixelEnabled = !!PIXEL_ID;

let initialized = false;

/**
 * Inject the Reddit pixel once and record the landing PageVisit.
 * No-op when unconfigured or already loaded.
 */
export function initRedditPixel(): void {
  if (initialized || !PIXEL_ID || typeof document === 'undefined') return;

  // Queue stub, transcribed from Reddit's own base code. Calls made before
  // pixel.js finishes loading are buffered on `callQueue` and replayed once
  // `sendEvent` exists; without it, an event fired early is simply lost.
  if (!window.rdt) {
    const stub = ((...args: unknown[]) => {
      if (stub.sendEvent) stub.sendEvent(...args);
      else (stub.callQueue = stub.callQueue || []).push(args);
    }) as RdtFn;
    stub.callQueue = [];
    window.rdt = stub;

    // Injection belongs inside this guard, as it does in Reddit's own snippet.
    // If someone later pastes the Events Manager snippet into public/index.html,
    // rdt already exists and loading pixel.js again would double every PageVisit
    // against an unchanged conversion count.
    const script = document.createElement('script');
    script.async = true;
    script.src = SRC;
    document.head.appendChild(script);
  }

  window.rdt('init', PIXEL_ID);
  // Reddit's snippet fires this itself as part of the base code. Unlike Plausible we
  // do NOT repeat it on SPA route changes: the pixel is here to attribute the signup
  // conversion, and a per-route PageVisit would inflate the top of Reddit's funnel
  // against a conversion count that only ever fires once.
  window.rdt('track', 'PageVisit');

  // Latched last: if injection above threw, a retry is still possible rather than
  // leaving a buffering stub with no script coming to drain it.
  initialized = true;
}

/**
 * Record a conversion, e.g. trackRedditEvent('SignUp').
 *
 * Only through a pixel this module loaded. AdPixelGate decides whether it loads (US,
 * signed out, marketing page, no Global Privacy Control), and a visitor it declined to
 * load for must not be reported anyway because some other script left a window.rdt
 * lying around. (#812)
 */
export function trackRedditEvent(name: RedditEvent): void {
  if (!PIXEL_ID || !initialized) return;
  window.rdt?.('track', name);
}
